import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CartProps {
  isOpen: boolean;
  onClose: () => void;
  items: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
    image: string;
  }>;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onClear: () => void;
  isFreshFish?: boolean;
  onOrderSent?: () => void;
}

export default function Cart({ isOpen, onClose, items, onUpdateQuantity, onClear, isFreshFish = false, onOrderSent }: CartProps) {
  const { t, i18n } = useTranslation('common');
  const [comment, setComment] = useState('');
  const [recipientInfo, setRecipientInfo] = useState('');

  // Calcul du total en excluant les produits "Sur commande" ou "Sur demande"
  const total = items.reduce((sum, item) => {
    return sum + (item.price * item.quantity);
  }, 0);

  // Vérifier s'il y a des produits sans prix fixe
  const hasVariablePriceItems = false;

  const sendToWhatsApp = () => {
    // Séparer les produits frais et braisés
    const freshFishItems = items.filter(item => item.id?.startsWith('ff'));
    const grilledFishItems = items.filter(item => item.id?.startsWith('gf'));
    
    let message = "";
    
    // Message pour les poissons frais
    if (freshFishItems.length > 0) {
      message += `🐟 *${t('whatsapp.freshOrder')}*\n\n`;
      freshFishItems.forEach(item => {
        // Tous les poissons frais sont au kg
        const unit = t('cart.kg');
        const priceUnit = t('cart.kg');
        
        message += `• ${item.name}\n`;
        message += `  ${t('whatsapp.quantity')}: ${item.quantity} ${unit}\n`;
        message += `  ${t('whatsapp.unitPrice')}: ${item.price.toLocaleString()} FCFA/${priceUnit}\n`;
        message += `  ${t('whatsapp.total')}: ${(item.price * item.quantity).toLocaleString()} FCFA\n\n`;
      });
    }
    
    // Message pour les poissons braisés
    if (grilledFishItems.length > 0) {
      if (freshFishItems.length > 0) {
        message += "\n";
      }
      message += `🔥 *${t('whatsapp.grilledOrder')}*\n\n`;
      grilledFishItems.forEach(item => {
        message += `• ${item.name}\n`;
        message += `  ${t('whatsapp.quantity')}: ${item.quantity} ${t('cart.pieces')}\n`;
        message += `  ${t('whatsapp.unitPrice')}: ${item.price.toLocaleString()} FCFA/${t('cart.pieces')}\n`;
        message += `  ${t('whatsapp.total')}: ${(item.price * item.quantity).toLocaleString()} FCFA\n\n`;
      });
    }
    
    message += `*${t('whatsapp.total')}: ${total.toLocaleString()} FCFA*\n\n`;
    
    // Ajouter les informations du destinataire
    if (recipientInfo.trim()) {
      message += `👤 *${t('cart.recipientInfo')}:*\n${recipientInfo.trim()}\n\n`;
    }
    
    // Ajouter le commentaire s'il existe
    if (comment.trim()) {
      message += `📝 *${t('whatsapp.comment')}:*\n${comment.trim()}\n\n`;
    }
    
    // Ajouter les numéros de paiement
    message += `💳 *${t('cart.paymentNumbers')}:*\n`;
    message += `• Orange Money: 658538767\n`;
    message += `• MTN Mobile Money: 654391120\n\n`;
    
    message += `⚠️ *${t('whatsapp.priceNote')}*\n\n`;
    message += t('whatsapp.confirmOrder');
    
    // Toutes les commandes vers le numéro WhatsApp
    const whatsappUrl = `https://wa.me/237654391120?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    
    // Réinitialiser les champs et notifier le parent
    setComment('');
    setRecipientInfo('');
    if (onOrderSent) {
      onOrderSent();
    }
  };

  const formatPrice = (item: any) => {
    // Pour les crabes (prix au kg)
    if (item.id === 'ff10') {
      return `${item.price.toLocaleString()} ${t('freshFish.perKg')}`;
    }
    
    // Pour les poissons frais (prix au kg)
    if (item.id?.startsWith('ff')) {
      return `${item.price.toLocaleString()} ${t('freshFish.perKg')}`;
    }
    
    // Pour les autres poissons braisés (prix unitaire)
    return `${item.price.toLocaleString()} ${t('freshFish.perPiece')}`;
  };

  const calculateItemTotal = (item: any) => {
    return `${(item.price * item.quantity).toLocaleString()} FCFA`;
  };

  const handleQuantityChange = (id: string, newQuantity: number) => {
    if (newQuantity > 0 && newQuantity <= 100) {
      onUpdateQuantity(id, newQuantity);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      ></div>
      <div className="fixed right-0 top-0 h-full w-full sm:max-w-md bg-white shadow-2xl z-50 flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('cart.title')}</h2>
          <button 
            onClick={onClose}
            className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl sm:text-2xl text-gray-600"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-shopping-cart-line text-5xl sm:text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-base sm:text-lg">{t('cart.empty')}</p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {items.map(item => (
                <div key={item.id} className="flex gap-3 sm:gap-4 bg-gray-50 rounded-xl p-3 sm:p-4">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-16 h-16 sm:w-20 sm:h-20 object-cover object-top rounded-lg flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900 text-sm truncate">{item.name}</h4>
                    <div className="text-gray-600 text-xs mt-1">
                      <div className="mt-1">{t('cart.unitPrice')}: {formatPrice(item)}</div>
                    </div>
                    
                    {/* Contrôle de quantité */}
                    <div className="mt-2 flex items-center gap-2">
                      {item.id?.startsWith('ff') ? (
                        // Pour les poissons frais : ajustement en kg
                        <>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity - 0.5)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors cursor-pointer"
                          >
                            <i className="ri-subtract-line text-sm"></i>
                          </button>
                          <input
                            type="number"
                            min="0.5"
                            max="100"
                            step="0.5"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseFloat(e.target.value);
                              if (!isNaN(val)) {
                                handleQuantityChange(item.id, val);
                              }
                            }}
                            className="w-16 text-center border border-gray-300 rounded-lg py-1 text-sm font-semibold"
                          />
                          <span className="text-xs text-gray-600">{t('cart.kg')}</span>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity + 0.5)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-600 hover:bg-teal-700 text-white transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-sm"></i>
                          </button>
                        </>
                      ) : (
                        // Pour les poissons braisés : ajustement en pièces
                        <>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors cursor-pointer"
                          >
                            <i className="ri-subtract-line text-sm"></i>
                          </button>
                          <input
                            type="number"
                            min="1"
                            max="100"
                            step="1"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val)) {
                                handleQuantityChange(item.id, val);
                              }
                            }}
                            className="w-16 text-center border border-gray-300 rounded-lg py-1 text-sm font-semibold"
                          />
                          <span className="text-xs text-gray-600">{t('cart.pieces')}</span>
                          <button
                            onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-600 hover:bg-teal-700 text-white transition-colors cursor-pointer"
                          >
                            <i className="ri-add-line text-sm"></i>
                          </button>
                        </>
                      )}
                    </div>
                    
                    <div className="font-semibold text-gray-900 text-sm mt-2">
                      {t('whatsapp.total')}: {calculateItemTotal(item)}
                    </div>
                  </div>
                  <button
                    onClick={() => onUpdateQuantity(item.id, 0)}
                    className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-red-500 text-sm sm:text-base"></i>
                  </button>
                </div>
              ))}
              
              {/* Champ pour le destinataire */}
              <div className="mt-4 sm:mt-6">
                <label htmlFor="recipient-info" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-user-line mr-1"></i>
                  {t('cart.recipientInfo')}
                </label>
                <textarea
                  id="recipient-info"
                  value={recipientInfo}
                  onChange={(e) => setRecipientInfo(e.target.value)}
                  placeholder={t('cart.recipientPlaceholder')}
                  rows={2}
                  maxLength={200}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {recipientInfo.length}/200 {t('cart.characters')}
                </div>
              </div>
              
              {/* Champ de commentaire */}
              <div className="mt-4">
                <label htmlFor="cart-comment" className="block text-sm font-semibold text-gray-700 mb-2">
                  <i className="ri-message-3-line mr-1"></i>
                  {t('cart.comment')}
                </label>
                <textarea
                  id="cart-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('cart.commentPlaceholder')}
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
                <div className="text-xs text-gray-500 mt-1 text-right">
                  {comment.length}/500 {t('cart.characters')}
                </div>
              </div>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-4 sm:p-6 space-y-3 sm:space-y-4">
            {/* Mention sur la variation des prix */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <i className="ri-information-line text-amber-600 text-base mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-amber-800">
                  {t('cart.priceNote')}
                </p>
              </div>
            </div>

            {/* Numéros de paiement */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <i className="ri-bank-card-line text-teal-600 text-base mt-0.5 flex-shrink-0"></i>
                <div className="flex-1">
                  <p className="text-xs font-semibold text-teal-900 mb-1">{t('cart.paymentNumbers')}:</p>
                  <p className="text-xs text-teal-800">Orange Money: <span className="font-semibold">658538767</span></p>
                  <p className="text-xs text-teal-800">MTN Mobile Money: <span className="font-semibold">654391120</span></p>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <span className="text-base sm:text-lg font-semibold text-gray-700">{t('cart.total')}</span>
              <span className="text-xl sm:text-2xl font-bold text-teal-600">
                {total.toLocaleString()} FCFA
              </span>
            </div>
            <button
              onClick={sendToWhatsApp}
              className="w-full bg-green-600 text-white py-3 sm:py-4 rounded-full text-sm sm:text-base font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
              {t('cart.finalizeWhatsApp')}
            </button>
            <button
              onClick={onClear}
              className="w-full bg-gray-100 text-gray-700 py-2.5 sm:py-3 rounded-full text-sm sm:text-base font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('cart.clearCart')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
